import React from 'react';
import {
  typeMeta,
  latestTrainedVersion,
  formatCount,
  timeAgo,
  formatDate,
  ownerInitials,
  ownerHue,
  ownerKey,
} from '../hubMeta';

const HubModelCard = ({ model, onOpen, onSelectOwner, onQuickDownload, downloadingId }) => {
  const meta = typeMeta(model.model_type);
  const latest = latestTrainedVersion(model);
  const downloading = latest && downloadingId === latest._id;
  const versions = model.version_count ?? (model.versions?.length || 0);
  const owner = model.owner_name || 'Unknown';
  const updated = timeAgo(model.created_on);

  return (
    <article
      className="hub-card"
      onClick={() => onOpen(model)}
      role="button"
      tabIndex={0}
      aria-label={`${model.model_name} — view details`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(model);
        }
      }}
    >
      <span className="hub-card__open" aria-hidden>
        <i className="fa fa-external-link-alt" />
      </span>

      <div className="hub-card__top">
        <div className={`hub-card__icon hub-card__icon--${model.model_type}`}>
          <i className={`fa ${meta.icon}`} aria-hidden />
        </div>
        <div className="hub-card__title-wrap">
          <h3 className="hub-card__title" title={model.model_name}>
            {model.model_name}
          </h3>
          <div className="hub-card__subrow">
            <span className={`hub-type-pill hub-type-pill--${model.model_type}`}>
              <i className={`fa ${meta.icon}`} aria-hidden /> {meta.label}
            </span>
            {updated && (
              <span className="hub-card__updated" title={`Published ${formatDate(model.created_on)}`}>
                <i className="fa fa-clock" aria-hidden /> {updated}
              </span>
            )}
          </div>
        </div>
      </div>

      <p className="hub-card__detail">
        {model.model_detail || 'No description provided.'}
      </p>

      {Array.isArray(model.tags) && model.tags.length > 0 && (
        <div className="hub-card__tags">
          {model.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="hub-tag">
              {tag}
            </span>
          ))}
          {model.tags.length > 3 && <span className="hub-tag hub-tag--more">+{model.tags.length - 3}</span>}
        </div>
      )}

      <div className="hub-card__meta">
        <span className="hub-signal" title="Downloads">
          <i className="fa fa-download" aria-hidden /> {formatCount(model.download_count)}
          <span className="hub-signal__unit">downloads</span>
        </span>
        <span className="hub-signal__sep" aria-hidden>·</span>
        <span className="hub-signal" title="Versions">
          <i className="fa fa-code-branch" aria-hidden /> {versions}
          <span className="hub-signal__unit">{versions === 1 ? 'version' : 'versions'}</span>
        </span>
      </div>

      <div className="hub-card__footer">
        {/* The visible text is just the name, so spell out what the button does. */}
        <button
          type="button"
          className="hub-card__owner"
          title={`Show only ${owner}'s models`}
          aria-label={`Show only ${owner}'s models`}
          onClick={(e) => {
            e.stopPropagation(); // the whole card opens the drawer
            onSelectOwner?.(model);
          }}
        >
          <span
            className="hub-avatar"
            style={{ background: `hsl(${ownerHue(ownerKey(model))} 55% 42%)` }}
            aria-hidden
          >
            {ownerInitials(owner)}
          </span>
          <span className="hub-card__owner-name">{owner}</span>
        </button>

        {latest ? (
          <button
            type="button"
            className="hub-card__download"
            disabled={downloading}
            onClick={(e) => {
              e.stopPropagation();
              onQuickDownload(model, latest);
            }}
            title="Download the latest trained version"
          >
            {downloading ? (
              <i className="fa fa-spinner fa-spin" aria-hidden />
            ) : (
              <i className="fa fa-download" aria-hidden />
            )}{' '}
            Download
          </button>
        ) : (
          <span className="hub-card__untrained" title="No trained version available yet">
            <i className="fa fa-hourglass-half" aria-hidden /> Untrained
          </span>
        )}
      </div>
    </article>
  );
};

export default HubModelCard;
